import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _typeof from '@babel/runtime/helpers/esm/typeof';
import escapeStringRegex from 'escape-string-regexp';
import omit from 'lodash.omit';
import assert from 'nanoassert';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import isEqual from 'lodash.isequal';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import { useState, useCallback } from 'react';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';

var wildcardPathRegex = /\[[^\]]*\]/g;
var buildUrlObject = function buildUrlObject() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$params = _ref.params,
    params = _ref$params === void 0 ? {} : _ref$params,
    route = _ref.route;
  assert(_typeof(params) === 'object', "'params' must be an object, got <".concat(_typeof(params), ">").concat(params));
  assert(typeof route === 'string', "'route' must be a string, got <".concat(_typeof(route), ">").concat(route));
  assert(!!route, "'route' must be a valid path to a route in the pages/ folder, got ".concat(route));
  var urlObject = Object.entries(params).reduce(function (result, _ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
      param = _ref3[0],
      value = _ref3[1];
    var pathFragmentRegex = new RegExp("\\[".concat(escapeStringRegex(param), "\\]"), 'g');
    var matches = result.pathname.match(pathFragmentRegex);
    if (matches && matches.length) {
      return {
        pathname: result.pathname.replace(pathFragmentRegex, value),
        query: omit(result.query, param)
      };
    }
    return result;
  }, {
    pathname: route,
    query: params
  });
  var danglingFragments = urlObject.pathname.match(wildcardPathRegex);
  if (danglingFragments && danglingFragments.length) {
    throw new Error("Did not receive expected parameters ".concat(danglingFragments.join(', '), " for route '").concat(route, "'"));
  }
  return urlObject;
};

var isAbsoluteUri = function isAbsoluteUri() {
  var uri = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  // Derived from: https://stackoverflow.com/a/31991870/473961
  return new RegExp('^(?:[a-z][a-z0-9+.-]*:|//)', 'i').test(uri);
};

var getRedirectUrl = function getRedirectUrl(uri) {
  var fallback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '/';
  // Ignore absolute URIs to avoid an attack vector for CSRF
  if (!uri || isAbsoluteUri(uri)) {
    return fallback;
  }
  return uri;
};

var stringifyUrlObject = function stringifyUrlObject() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$pathname = _ref.pathname,
    pathname = _ref$pathname === void 0 ? '' : _ref$pathname,
    _ref$query = _ref.query,
    query = _ref$query === void 0 ? {} : _ref$query;
  assert(!pathname || typeof pathname === 'string', "'pathname' must be a string, got <".concat(_typeof(pathname), ">").concat(pathname));
  assert(_typeof(query) === 'object', "'query' must be an object, got <".concat(_typeof(query), ">").concat(query));
  if (query && Object.keys(query).length > 0) {
    return "".concat(pathname || '', "?").concat(new URLSearchParams(query).toString());
  }
  return pathname || '';
};

var getProductVariantAttributes = function getProductVariantAttributes(product) {
  var _product$variants;
  var variantAttributes = {};
  (_product$variants = product.variants) === null || _product$variants === void 0 || _product$variants.forEach(function (variant) {
    variant.attributes.forEach(function (variantAttribute) {
      var variantAttributeId = variantAttribute.attribute.id;
      var variantAttributeValue = variantAttribute.values[0];
      if (Object.prototype.hasOwnProperty.call(variantAttributes, variantAttributeId)) {
        variantAttributes[variantAttributeId].values[variantAttributeValue.id] = variantAttributeValue;
      } else {
        variantAttributes[variantAttributeId] = {
          attribute: variantAttribute.attribute,
          values: _defineProperty({}, variantAttributeValue.id, variantAttributeValue)
        };
      }
    });
  });
  return variantAttributes;
};
var findVariantsForAttributeValues = function findVariantsForAttributeValues(product, attributeValues) {
  // Find the varirant where ...
  return product.variants.filter(function (variant) {
    return (
      // all of our attribute choices
      Object.entries(attributeValues).filter(function (_ref) {
        var _ref2 = _slicedToArray(_ref, 2),
          attributeValueId = _ref2[1];
        return !!attributeValueId;
      }).every(function (_ref3) {
        var _ref4 = _slicedToArray(_ref3, 2),
          attributeId = _ref4[0],
          attributeValueId = _ref4[1];
        // exist on the variant
        return variant.attributes.find(function (attribute) {
          return attribute.attribute.id === attributeId && attribute.values[0].id === attributeValueId;
        });
      })
    );
  });
};

function stripUrl(url) {
  return url.split(/[?#]/)[0];
}

function getFilenameFromURL(url) {
  var stripped = stripUrl(url);
  return stripped.split('/').pop();
}

var areSameAddress = function areSameAddress(a, b) {
  var strippedA = {
    firstName: a.firstName,
    lastName: a.lastName,
    streetAddress1: a.streetAddress1,
    streetAddress2: a.streetAddress2,
    city: a.city,
    countryArea: a.countryArea,
    country: a.country,
    postalCode: a.postalCode,
    phone: a.phone
  };
  var strippedB = {
    firstName: b.firstName,
    lastName: b.lastName,
    streetAddress1: b.streetAddress1,
    streetAddress2: b.streetAddress2,
    city: b.city,
    countryArea: b.countryArea,
    country: b.country,
    postalCode: b.postalCode,
    phone: b.phone
  };
  return isEqual(strippedA, strippedB);
};

var usePricing = function usePricing(item) {
  var _pricing$retailPrice$, _pricing$retailPrice, _pricing$userDiscount, _pricing$retailPrice2, _pricing$userDiscount2, _pricing$userDiscount3;
  if (!item) return {};
  var pricing = item === null || item === void 0 ? void 0 : item.pricing;
  if (!pricing) return {};
  var currency = (_pricing$retailPrice$ = pricing === null || pricing === void 0 || (_pricing$retailPrice = pricing.retailPrice) === null || _pricing$retailPrice === void 0 ? void 0 : _pricing$retailPrice.currency) !== null && _pricing$retailPrice$ !== void 0 ? _pricing$retailPrice$ : pricing === null || pricing === void 0 || (_pricing$userDiscount = pricing.userDiscountedPrice) === null || _pricing$userDiscount === void 0 ? void 0 : _pricing$userDiscount.currency;
  var undiscountedPrice = pricing === null || pricing === void 0 || (_pricing$retailPrice2 = pricing.retailPrice) === null || _pricing$retailPrice2 === void 0 ? void 0 : _pricing$retailPrice2.net.amount;
  var discountedPrice = undiscountedPrice !== (pricing === null || pricing === void 0 || (_pricing$userDiscount2 = pricing.userDiscountedPrice) === null || _pricing$userDiscount2 === void 0 ? void 0 : _pricing$userDiscount2.net.amount) ? pricing === null || pricing === void 0 || (_pricing$userDiscount3 = pricing.userDiscountedPrice) === null || _pricing$userDiscount3 === void 0 ? void 0 : _pricing$userDiscount3.net.amount : undefined;
  var undiscountedPriceFormatted = undiscountedPrice ? new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(undiscountedPrice).replace(/(\.|,)00$/g, '') : null;
  var discountedPriceFormatted = discountedPrice ? new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(discountedPrice).replace(/(\.|,)00$/g, '') : null;
  return {
    undiscountedPrice: undiscountedPriceFormatted,
    discountedPrice: discountedPriceFormatted,
    currency: currency
  };
};

var getRecordHref = function getRecordHref(type, slug) {
  return "/".concat(type, "/").concat(slug);
};
var getLinkedObjectHref = function getLinkedObjectHref(_ref) {
  var category = _ref.category,
    collection = _ref.collection,
    brand = _ref.brand,
    page = _ref.page,
    url = _ref.url;
  var pluralTypeAndLinkedObject = Object.entries({
    categories: category,
    collections: collection,
    pages: page,
    brands: brand
  }).find(function (_ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
      value = _ref3[1];
    return !!value;
  });
  var route = '';
  if (url) {
    route = url;
  } else {
    if (!pluralTypeAndLinkedObject) return null;
    var _pluralTypeAndLinkedO = _slicedToArray(pluralTypeAndLinkedObject, 2),
      type = _pluralTypeAndLinkedO[0],
      objectOrSlug = _pluralTypeAndLinkedO[1];
    var slug = typeof objectOrSlug == 'string' ? objectOrSlug : objectOrSlug.slug;
    route = getRecordHref(type, slug);
  }
  return route;
};

function getBlurUrl(url) {
  var w = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '50';
  var newUrl = new URL(url);
  var params = newUrl.searchParams;
  params.set('w', w);
  params.set('blur', Math.max(1, w / 3).toFixed(0));
  params.set('px', Math.max(1, w / 12).toFixed(0));
  newUrl.search = params.toString();
  return newUrl.toString();
}

//by Eamon Nerbonne (from https://eamonnerbonne.github.io/a-vs-an/), Apache 2.0 license
var AnVsASimple = function (root) {
  var dict = "2h.#2.a;i;&1.N;*4.a;e;i;o;/9.a;e;h1.o.i;l1./;n1.o.o;r1.e.s1./;01.8;12.1a;01.0;12.8;9;2.31.7;4.5.6.7.8.9.8a;0a.0;1;2;3;4;5;6;7;8;9;11; .22; .–.31; .42; .–.55; .,.h.k.m.62; .k.72; .–.82; .,.92; .–.8;<2.m1.d;o;=1.=1.E;@;A6;A1;A1.S;i1;r1;o.m1;a1;r1; .n1;d1;a1;l1;u1;c1.i1.a1.n;s1;t1;u1;r1;i1;a1;s.t1;h1;l1;e1;t1;e1.s;B2.h2.a1.i1;r1;a.á;o1.r1.d1. ;C3.a1.i1.s1.s.h4.a2.i1.s1;e.o1.i;l1.á;r1.o1.í;u2.i;r1.r1.a;o1.n1.g1.j;D7.a1.o1.q;i2.n1.a1.s;o1.t;u1.a1.l1.c;á1. ;ò;ù;ư;E7;U1;R.b1;o1;l1;i.m1;p1;e1;z.n1;a1;m.s1;p5.a1.c;e;h;o;r;u1.l1;o.w1;i.F11. ;,;.;/;0;1;2;3;4;5;6;71.0.8;9;Ae;B.C.D.F.I2.L.R.K.L.M.N.P.Q.R.S.T.B;C1;M.D;E2.C;I;F1;r.H;I3.A1;T.R1. ;U;J;L3.C;N;P;M;O1. ;P1;..R2.A1. ;S;S;T1;S.U2.,;.;X;Y1;V.c;f1.o.h;σ;G7.e1.r1.n1.e;h1.a3.e;i;o;i1.a1.n1.g;o2.f1. ;t1.t1. ;r1.i1.a;w1.a1.r1.r;ú;Hs. ;&;,;.2;A.I.1;2;3;5;7;B1;P.C;D;F;G;H1;I.I6;C.G.N.P.S1.D;T.K1.9;L;M1;..N;O2. ;V;P;R1;T.S1.F.T;V;e2.i1.r;r1.r1.n;o2.n6;d.e1.s;g.k.o2;l.r1;i1.f;v.u1.r;I3;I2;*.I.n1;d1;e1;p1;e1;n1;d2;e1;n1;c1;i.ê.s1;l1;a1;n1;d1;s.J1.i1.a1.o;Ly. ;,;.;1;2;3;4;8;A3. ;P;X;B;C;D;E2. ;D;F1;T.G;H1.D.I1.R;L;M;N;P;R;S1;m.T;U1. ;V1;C.W1.T;Z;^;a1.o1.i1.g;o1.c1.h1.a1;b.p;u1.s1.h1;o.ộ;M15. ;&;,;.1;A1;.1;S./;1;2;3;4;5;6;7;8;Ai;B.C.D.F.G.J.L.M.N.P.R.S.T.V.W.X.Y.Z.B1;S1;T.C;D;E3.P1;S.W;n;F;G;H;I4. ;5;6;T1;M.K;L;M;N;O1.U;P;Q;R;S;T1;R.U2. ;V;V;X;b1.u1.m;f;h;o2.D1.e.U1;..p1.3;s1.c;Ny. ;+;.1.E.4;7;8;:;A3.A1;F.I;S1.L;B;C;D;E3.A;H;S1. ;F1;U.G;H;I7.C.D1. ;K.L.N.O.S.K;L;M1;M.N2.R;T;P1.O1.V1./1.B;R2;J.T.S1;W.T1;L1.D.U1.S;V;W2.A;O1.H;X;Y3.C1.L;P;U;a1.s1.a1.n;t1.h;v;²;×;O5;N1;E.l1;v.n2;c1.e.e1.i;o1;p.u1;i.P1.h2.i1.a;o2.b2;i.o.i;Q1.i1.n1.g1.x;Rz. ;&;,;.1;J./;1;4;6;A3. ;.;F1;T.B1;R.C;D;E3. ;S1.P;U;F;G;H1.S;I2.A;C1. ;J;K;L1;P.M5;1.2.3.5.6.N;O2.H;T2;A.O.P;Q;R1;F.S4;,...?.T.T;U4;B.M.N.S.V;X;c;f1;M1...h2.A;B;ò;S11. ;&;,;.4.E;M;O;T1..3.B;D;M;1;3;4;5;6;8;9;A3. ;8;S2;E.I.B;C3.A1. ;R2.A.U.T;D;E6. ;5;C3;A.O.R.I1.F.O;U;F3;&.H.O1.S.G1;D.H3.2;3;L;I2. ;S1.O.K2.I.Y.L3;A2. ;.;I1. ;O.M3;A1. ;I.U1.R.N5.A.C3.A.B.C.E.F.O.O5. ;A1.I;E;S1;U.V;P7;A7;A.C.D.M.N.R.S.E1. ;I4;C.D.N.R.L1;O.O.U.Y.Q1. ;R;S1;W.T9.A1. ;C;D;F;I;L;M;S;V;U7.B.L.M.N.P.R.S.V;W1.R;X1.M;h1.i1.g1.a1.o;p1.i1.o1;n.t2.B;i1.c1.i;T4.a2.i2.g1.a.s1.c;v1.e1.s;e1.a1.m1.p;u1.i2.l;r;à;Um..1.N1..1.C;/1.1;11. .21.1;L1.T;M1.N;N4.C1.L;D2. .P.K;R1. .a;b2;a.i.d;g1.l;i1.g.l2;i.y.m;no. ;a1.n.b;c;d;e1;s.f;g;h;i2.d;n;j;k;l;m;n;o;p;q;r;s;t;u;v;w;p;r3;a.e.u1.k;s3. ;h;t1;r.t4.h;n;r;t;x;z;í;W2.P1.:4.A1.F;I2.B;N1.H.O1.V;R1.F1.C2.N.U.i1.k1.i1.E1.l1.i;X7;a.e.h.i.o.u.y.Y3.e1.t1.h;p;s;[5.A;E;I;a;e;_2._1.i;e;`3.a;e;i;a7; .m1;a1;r1. .n1;d2; .ě.p1;r1;t.r1;t1;í.u1;s1;s1;i1. .v1;u1;t.d3.a1.s1. ;e2.m1. ;r1. ;i2.c1.h1. ;e1.s1.e2.m;r;e8;c1;o1;n1;o1;m1;i1;a.e1;w.l1;i1;t1;e1;i.m1;p1;e1;z.n1;t1;e1;n1;d.s2;a1. .t4;a1; .e1; .i1;m1;a1;r.r1;u1.t.u1.p1. ;w.f3. ;M;y1.i;h9. ;,;.;C;a1.u1.t1;b.e2.i1.r1;a.r1.m1.a1.n;o4.m2.a1; .m;n8; .b.d.e3; .d.y.g.i.k.v.r1.s1. ;u1.r;r1. ;t1;t1;p1;:.i6;b1;n.e1;r.n2;f2;l1;u1;ê.o1;a.s1;t1;a1;l1;a.r1; .s1; .u.k1.u1. ;l3.c1.d;s1. ;v1.a;ma. ;,;R;b1.a.e1.i1.n;f;p;t1.a.u1.l1.t1.i1.c1.a1.m1.p1.i;×;n6. ;V;W;d1; .t;×;o8;c2;h1;o.u1;p.d1;d1;y.f1; .g1;g1;i.no. ;';,;/;a;b;c1.o;d;e2.i;r;f;g;i;l;m;n;o;r;s;t;u;w;y;z;–;r1;i1;g1;e.t1;r1.s;u1;i.r3. ;&;f;s9.,;?;R;f2.e.o.i1.c1.h;l1. ;p2.3;i1. ;r1.g;v3.a.e.i.t2.A;S;uc; ...b2.e;l;f.k2.a;i;m1;a1. .n3;a3; .n5.a;c;n;s;t;r1;y.e2; .i.i8.c2.o1.r1.p;u1.m;d1;i1.o;g1.n;l1.l;m1;o.n;s1.s;v1.o1;c.r5;a.e.i.l.o.s3. ;h;u1.r2;e.p3;a.e.i.t2.m;t;v.w1.a;xb. ;';,;.;8;b;k;l;m1;a.t;y1. ;y1.l;{1.a;|1.a;£1.8;À;Á;Ä;Å;Æ;É;Ò;Ó;Ö;Ü;à;á;æ;è;é1;t3.a;o;u;í;ö;ü1; .Ā;ā;ī;İ;Ō;ō;œ;Ω;α;ε;ω;ϵ;е;–2.e;i;ℓ;";
  function fill(node) {
    var kidCount = parseInt(dict, 36) || 0,
      offset = kidCount && kidCount.toString(36).length;
    node.article = dict[offset] === '.' ? 'a' : 'an';
    dict = dict.substr(1 + offset);
    for (var i = 0; i < kidCount; i++) {
      var kid = node[dict[0]] = {};
      dict = dict.substr(1);
      fill(kid);
    }
  }
  fill(root);
  return {
    raw: root,
    //Usage example: AvsAnSimple.query("example")
    //example returns: "an"
    query: function query(word) {
      var node = root,
        sI = 0,
        result,
        c;
      do {
        c = word[sI++];
      } while ('"‘’“”$\'-('.indexOf(c) >= 0); //also terminates on end-of-string "undefined".

      while (1) {
        result = node.article || result;
        node = node[c];
        if (!node) return result;
        c = word[sI++] || ' ';
      }
    }
  };
}({});
var getIndefiniteArticle = AnVsASimple.query;

var localizeUrl = function localizeUrl(url) {
  var defaultBase = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'https://outdoorly.com';
  var apiUrl = new URL(process.env.NEXT_PUBLIC_API_URL);
  return new URL(url, (typeof window === "undefined" ? "undefined" : _typeof(window)) !== undefined ? "".concat(apiUrl.origin) : defaultBase).toString();
};

var _dirtyValues = function dirtyValues(dirtyFields, allValues) {
  // NOTE: Recursive function.

  // If *any* item in an array was modified, the entire array must be submitted, because there's no
  // way to indicate "placeholders" for unchanged elements. `dirtyFields` is `true` for leaves.
  if (dirtyFields === true || Array.isArray(dirtyFields)) {
    return allValues;
  }

  // Here, we have an object.
  return Object.keys(dirtyFields).map(function (key) {
    return [key, _dirtyValues(dirtyFields[key], allValues[key])];
  }).reduce(function (previous, _ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      k = _ref2[0],
      v = _ref2[1];
    return _objectSpread(_objectSpread({}, previous), {}, _defineProperty({}, k, v));
  }, {});
};

/**
 * returns a function for the loader prop of NextImage
 * @param {string} url the url of the image we're returning a loader for
 * @returns {function} loader function for NextJS
 */
var getImageLoader = function getImageLoader(url) {
  return typeof url !== 'string' ? undefined : imgixRegExp.test(url) ? imgixLoader : googleRegExp.test(url) ? googleLoader : undefined;
};
var imgixRegExp = /imgix\.net\//;
var imgixLoader = function imgixLoader(_ref) {
  var src = _ref.src,
    width = _ref.width,
    quality = _ref.quality;
  return "".concat(src, "?w=").concat(width, "&q=").concat(quality || 75, "&auto=format");
};
var googleRegExp = /storage\.googleapis\.com\//;
var googleLoader = function googleLoader(_ref2) {
  var src = _ref2.src;
  return src;
};

/**
 * for functions that return a promise, use `useDebouncePromise`
 * @param func the function to debounce
 * @param timeoutLength the length to wait before calling
 * @returns a function that returns void and will call the supplied `func` automatically after the timeout
 */
var useDebounce = function useDebounce(func, timeoutLength) {
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    timeout = _useState2[0],
    setTimeoutId = _useState2[1];
  var debounceFunc = useCallback(function () {
    if (timeout) {
      clearTimeout(timeout);
    }
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    setTimeoutId(setTimeout.apply(void 0, [func, timeoutLength].concat(args)));
  }, [func, timeoutLength, timeout]);
  return debounceFunc;
};
/**
 * for functions that don't return a promise, use `useDebounce`
 * @param func the function to debounce, is called with args after timeoutLength
 * @param timeoutLength (ms) length of time to wait before calling supplied function
 * @returns function to call with arguments for debounce
 */
var useDebouncePromise = function useDebouncePromise(func, timeoutLength) {
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    timeout = _useState4[0],
    setTimeoutId = _useState4[1];
  var debouncePromise = useCallback(function () {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    return new Promise(function (res) {
      if (timeout) {
        clearTimeout(timeout);
      }
      setTimeoutId(setTimeout.apply(void 0, [function (arg) {
        return func(arg).then(function (resp) {
          return res(resp);
        });
      }, timeoutLength].concat(args)));
    });
  }, [func, timeoutLength, setTimeoutId, timeout]);
  return debouncePromise;
};

var getCarouselsByPosition = function getCarouselsByPosition(nodes) {
  var _nodes$filter$reduce;
  return (_nodes$filter$reduce = nodes === null || nodes === void 0 ? void 0 : nodes.filter(function (node) {
    var _node$carousel$featur;
    return (_node$carousel$featur = node.carousel.featureCarousels) === null || _node$carousel$featur === void 0 ? void 0 : _node$carousel$featur.edges.length;
  }).reduce(function (acc, node) {
    return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, node.position, _objectSpread(_objectSpread({}, node.carousel), {}, {
      elementType: 'Features',
      paginate: true
    })));
  }, {})) !== null && _nodes$filter$reduce !== void 0 ? _nodes$filter$reduce : {};
};

var buildProductFeatureGridElements = function buildProductFeatureGridElements(products, featureCarouselsByPosition) {
  return products.reduce(function (_ref, product, index, array) {
    var currentPosition = _ref.currentPosition,
      elements = _ref.elements;
    var newElements = [];
    while (featureCarouselsByPosition[currentPosition]) {
      // If there is a feature for this position, add it and increment our position by 2
      newElements.push({
        carousel: featureCarouselsByPosition[currentPosition]
      });
      currentPosition += 2;
    }
    newElements.push({
      product: product
    });

    // If there is just one feature left, show it
    if (index === array.length - 1 && featureCarouselsByPosition[currentPosition + 1]) newElements.push({
      carousel: featureCarouselsByPosition[currentPosition + 1]
    });
    return {
      currentPosition: currentPosition + 1,
      elements: [].concat(_toConsumableArray(elements), newElements)
    };
  }, {
    currentPosition: 1,
    elements: []
  }).elements;
};

export { areSameAddress, buildProductFeatureGridElements, buildUrlObject, _dirtyValues as dirtyValues, findVariantsForAttributeValues, getBlurUrl, getCarouselsByPosition, getFilenameFromURL, getImageLoader, getIndefiniteArticle, getLinkedObjectHref, getProductVariantAttributes, getRecordHref, getRedirectUrl, isAbsoluteUri, localizeUrl, stringifyUrlObject, stripUrl, useDebounce, useDebouncePromise, usePricing };
